/* global fetch */

import React, { useState } from 'react';
import querystring from 'querystring';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { css } from '@emotion/react';
import { Location } from '@birdi/types/location';
import { debounce } from 'lodash-es';
import google from './powered_by_google_on_white_hdpi.png';
import { location as locationAPI, stroot } from '@birdi/js-sdk';
import { Prediction } from '@birdi/js-sdk/src/public/location';

const formatPredictions = (predictions: Prediction[]) =>
  predictions.map((prediction) => ({
    value: prediction.place_id,
    label: prediction.description,
  }));

const getAddressComponentOfType = (addressComponents, type) =>
  addressComponents.filter((component) => component.types.includes(type));

const COORDS_OPTION_PREFIX = 'coords:';

// Recognise raw "lat, lng" input (Google Autocomplete returns no predictions
// for bare coordinates). Validated to real lat/lng ranges.
const parseCoordinates = (
  input: string,
): { lat: number; lng: number } | null => {
  const match = input
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const MenuList = (props) => {
  const { children } = props;
  return (
    <components.MenuList {...props}>
      {children}
      {Array.isArray(children) && (
        <div
          css={css`
            padding: 0.6rem 0.8rem;
          `}
        >
          <img
            css={css`
              max-width: 8rem;
            `}
            alt="Location lookup powered by Google"
            src={google}
          />
        </div>
      )}
    </components.MenuList>
  );
};

interface Geocode {
  location: Location;
}

interface LocationSelectProps {
  placeholder?: string;
  onLocationSelect: (geocode: Geocode) => void;
}

// API opts, see: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
export const LocationSelect = ({
  placeholder,
  placeTypes,
  defaultValue,
  onLocationSelect,
  onClear,
  isClearable,
  apiOpts = {},
  inputId,
  theme,
  instanceId = 'location-select',
  isLoadingOverride,
  disabled = false,
  styles = {},
  borderColor = '#e0e0e0',
  controlStyles = {},
  menuPortalTarget,
  autoFocus = false,
}: LocationSelectProps & { [key: string]: any }) => {
  const [formattedAddress, setFormattedAddress] = useState(defaultValue);
  const [isLoading, setLoadingState] = useState(false);
  const debouncedFetchPredictions = debounce((input: string, callback) => {
    locationAPI
      .searchLocations(stroot('pud5ch'), input, {
        query: apiOpts,
      })
      .then((response) => response.body)
      .then((body) => {
        callback(formatPredictions(body?.predictions ?? []));
      });
  }, 250);

  const fetchPredictions = (input, callback) => {
    if (!input) {
      return Promise.resolve({ options: [] });
    }
    // Raw coordinates → offer the point itself (Google can't predict these).
    const coords = parseCoordinates(input);
    if (coords) {
      callback([
        {
          value: `${COORDS_OPTION_PREFIX}${coords.lat},${coords.lng}`,
          label: `${coords.lat}, ${coords.lng}`,
        },
      ]);
      return undefined;
    }
    return debouncedFetchPredictions(input, callback);
  };

  const handleSelect = (selectedLocation) => {
    if (!selectedLocation) {
      setFormattedAddress(null);
      return onClear();
    }
    const { value: placeid } = selectedLocation;
    setFormattedAddress(selectedLocation.label);

    // Raw-coordinate selection: resolve directly (no Google lookup). Derive a
    // small default envelope around the point for the boundary. bbox order
    // matches the address path: [northeast, southwest].
    if (
      typeof placeid === 'string' &&
      placeid.startsWith(COORDS_OPTION_PREFIX)
    ) {
      const [lat, lng] = placeid
        .slice(COORDS_OPTION_PREFIX.length)
        .split(',')
        .map(Number);
      const delta = 0.005; // ~0.5–1 km box
      const location: Location = {
        name: selectedLocation.label,
        streetAddress: '',
        suburb: '',
        state: '',
        country: '',
        center: [lng, lat],
        bbox: [
          [lng + delta, lat + delta],
          [lng - delta, lat - delta],
        ],
      };
      onLocationSelect({ location });
      return;
    }

    setLoadingState(true);

    locationAPI
      .getLocation(stroot('nou8be'), placeid)
      .then((response) => response.body!)
      .then((geocode) => {
        if (!Object.keys(geocode).length) return;
        const center = [
          geocode.geometry.location.lng,
          geocode.geometry.location.lat,
        ] as [number, number];
        const bbox = [
          [
            geocode.geometry.viewport.northeast.lng,
            geocode.geometry.viewport.northeast.lat,
          ],
          [
            geocode.geometry.viewport.southwest.lng,
            geocode.geometry.viewport.southwest.lat,
          ],
        ] as [[number, number], [number, number]];
        const city = getAddressComponentOfType(
          geocode.address_components,
          'political',
        )[0];
        const state = getAddressComponentOfType(
          geocode.address_components,
          'administrative_area_level_1',
        )[0];
        const country = getAddressComponentOfType(
          geocode.address_components,
          'country',
        )[0];
        let address;
        if (city && city.long_name !== geocode.name) {
          address = geocode.name;
        }
        // TODO: Add ts definition
        const location: Location = {
          name: selectedLocation.label,
          suburb: city ? city.long_name : '',
          state: state ? state.short_name : '',
          country: country ? country.long_name : '',
          center,
          bbox,
        };
        onLocationSelect({
          location,
        });
        setLoadingState(false);
      });
  };

  return (
    <AsyncSelect
      menuPortalTarget={menuPortalTarget}
      autoFocus={autoFocus}
      loadOptions={fetchPredictions}
      onChange={handleSelect}
      placeholder={placeholder}
      isClearable={isClearable}
      placeTypes={placeTypes}
      isLoading={isLoading}
      value={
        formattedAddress && {
          label: formattedAddress,
          value: formattedAddress,
        }
      }
      components={{ MenuList }}
      theme={theme}
      inputId={inputId}
      css={css`
        min-width: 14rem;
        font-weight: normal;
      `}
      styles={{
        ...styles,
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
          pointerEvents: 'visible',
        }),
        control: (base) => ({
          ...base,
          height: '100%',
          borderRadius: '2px',
          border: `solid ${borderColor} 1px`,
          ...controlStyles,
        }),
      }}
      instanceId={instanceId}
      isDisabled={disabled}
    />
  );
};
